import {
  Box,
  FormControl,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Text,
} from '@chakra-ui/react';
import { Form, FormikProvider, useFormik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import {
  CustomFormErrorMessage,
  PrimaryButton,
  SecondaryButton,
} from '../../../../../components/custom';
import {
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
} from '../../../../../components/icons';
import { currencyUnformat } from '../../../../../helpers/utilityFunctions';
import useHandleAmountChange from '../../../../../hooks/useHandleAmountChange';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import { getTransactionsPendingApprovalAsync } from '../../../../../store/slices/transactionMandateV2/getPendingTransactionsSlice';

export default function AwaitingApprovalFilters() {
  const dispatch = useAppDispatch();

  const [filtersApplied, setFiltersApplied] = useState(false);

  const [minAmountError, setMinAmountError] = useState('');
  const [maxAmountError, setMaxAmountError] = useState('');

  const handleAmountChange = useHandleAmountChange();

  const { status } = useAppSelector(
    (state) => state.getTransactionsPendingApproval
  );

  const initialValues = {
    startDate: '',
    endDate: '',
    transactionType: '',
    minAmount: '',
    maxAmount: '',
  };

  const validationSchema = Yup.object().shape({
    startDate: Yup.date().max(new Date(), 'Start Date cannot be in the future'),
    endDate: Yup.date()
      .min(Yup.ref('startDate'), "End date can't be before start date")
      .max(new Date(), 'End Date cannot be in the future'),
    transactionType: Yup.string(),
    minAmount: Yup.string(),
    maxAmount: Yup.string(),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: ({
      endDate,
      maxAmount,
      minAmount,
      startDate,
      transactionType,
    }) => {
      setFiltersApplied(true);

      dispatch(
        getTransactionsPendingApprovalAsync({
          minAmount: minAmount === '' ? '' : currencyUnformat(minAmount),
          maxAmount: currencyUnformat(maxAmount),
          to: endDate.toString(),
          from: startDate.toString(),
          type: transactionType,
        })
      );
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    getFieldProps,
    resetForm,
    setFieldValue,
    values,
  } = formik;

  const handleResetFilters = () => {
    setFiltersApplied(false);
    dispatch(getTransactionsPendingApprovalAsync({}));
    resetForm();
  };

  return (
    <Box position="relative">
      <Icon
        viewBox="0 0 200 200"
        color="green.500"
        position="absolute"
        right="0"
        top="-1"
        display={filtersApplied ? 'initial' : 'none'}
      >
        <path
          fill="currentColor"
          d="M 100, 100 m -75, 0 a 75,75 0 1,0 150,0 a 75,75 0 1,0 -150,0"
        />
      </Icon>

      <Menu closeOnSelect={false}>
        <MenuButton>
          <Box
            padding="8px 12px"
            display="flex"
            justifyContent="center"
            alignItems="center"
            borderRadius="44px"
            border="1px solid #e0e3e6"
            cursor="pointer"
          >
            <AdjustmentsHorizontalIcon mr="2" />

            <Text fontSize=".875rem" mr="3">
              Filter
            </Text>
            <ChevronDownIcon />
          </Box>
        </MenuButton>
        <MenuList width="380px">
          <MenuItem
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            background="transparent"
            borderBottom="1px solid #EBEDEF"
            height="64px"
          >
            <Text fontSize=".875rem" fontWeight={500} lineHeight="171.429%">
              Filter
            </Text>

            <SecondaryButton
              as={Box}
              sx={{
                borderRadius: '4px',
                fontSize: '.875rem',
                padding: '8px 16px',
                height: '40px',
              }}
              onClick={handleResetFilters}
            >
              Reset
            </SecondaryButton>
          </MenuItem>
          <Box background="transparent" py="3">
            <FormikProvider value={formik}>
              <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <Box display="flex" alignItems="center" gap="2" mb="3">
                  <FormControl
                    id="startDate"
                    isInvalid={Boolean(touched.startDate && errors.startDate)}
                  >
                    <FormLabel>Start Date</FormLabel>
                    <Input
                      type="date"
                      onChange={(e) =>
                        setFieldValue('startDate', e.target.value)
                      }
                    />

                    <CustomFormErrorMessage>
                      {touched.startDate && String(errors.startDate)}
                    </CustomFormErrorMessage>
                  </FormControl>

                  <FormControl
                    id="endDate"
                    isInvalid={Boolean(touched.endDate && errors.endDate)}
                  >
                    <FormLabel>End Date</FormLabel>
                    <Input
                      type="date"
                      onChange={(e) => setFieldValue('endDate', e.target.value)}
                    />
                    <CustomFormErrorMessage>
                      {touched.endDate && String(errors.endDate)}
                    </CustomFormErrorMessage>
                  </FormControl>
                </Box>

                <FormControl
                  id="transactionType"
                  isInvalid={Boolean(
                    touched.transactionType && errors.transactionType
                  )}
                >
                  <FormLabel>Transaction Type</FormLabel>
                  <Select
                    placeholder="Select Option"
                    {...getFieldProps('transactionType')}
                  >
                    <option value="transfer">Transfer</option>
                    <option value="bill-payment">Bill Payment</option>
                    <option value="betting">Betting</option>
                  </Select>
                  <CustomFormErrorMessage>
                    {touched.transactionType && errors.transactionType}
                  </CustomFormErrorMessage>
                </FormControl>

                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap="2"
                  mt="3"
                >
                  <FormControl
                    id="minAmount"
                    isInvalid={Boolean(
                      touched.minAmount && (errors.minAmount || minAmountError)
                    )}
                  >
                    <FormLabel>Amount</FormLabel>
                    <InputGroup>
                      <InputLeftElement
                        pointerEvents="none"
                        children="₦"
                        height="100%"
                      />
                      <Input
                        {...getFieldProps('minAmount')}
                        onChange={(e) => {
                          handleAmountChange({
                            e,
                            setFieldValue,
                            fieldValue: 'minAmount',
                            setErrorMessage: setMinAmountError,
                            minimumAmount: 0,
                          });
                        }}
                        placeholder="0"
                      />
                    </InputGroup>
                    <CustomFormErrorMessage>
                      {touched.minAmount &&
                        (errors.minAmount || minAmountError)}
                    </CustomFormErrorMessage>
                  </FormControl>

                  <span style={{ marginTop: '25px' }}>&mdash;</span>

                  <FormControl
                    id="maxAmount"
                    isInvalid={Boolean(
                      touched.maxAmount && (errors.maxAmount || maxAmountError)
                    )}
                  >
                    <FormLabel>Amount</FormLabel>
                    <InputGroup>
                      <InputLeftElement
                        pointerEvents="none"
                        children="₦"
                        height="100%"
                      />
                      <Input
                        {...getFieldProps('maxAmount')}
                        onChange={(e) => {
                          handleAmountChange({
                            e,
                            setFieldValue,
                            fieldValue: 'maxAmount',
                            setErrorMessage: setMaxAmountError,
                            minimumAmount: 0,
                          });
                        }}
                        placeholder="0"
                      />
                    </InputGroup>
                    <CustomFormErrorMessage>
                      {touched.minAmount &&
                        (errors.minAmount || minAmountError)}
                    </CustomFormErrorMessage>
                  </FormControl>
                </Box>

                <PrimaryButton
                  disableElevation
                  isDisabled={
                    !values.endDate &&
                    !values.startDate &&
                    !values.minAmount &&
                    !values.maxAmount &&
                    !values.transactionType
                  }
                  type="submit"
                  isLoading={status === 'loading'}
                  w="full"
                  mt="6"
                >
                  Apply
                </PrimaryButton>
              </Form>
            </FormikProvider>
          </Box>
        </MenuList>
      </Menu>
    </Box>
  );
}
